/**
 * Hebrew is the source of truth for the key set: `en.ts` is typed against it,
 * so a key added here and forgotten there fails the build rather than
 * rendering a blank on someone's screen. There is no fallback locale.
 *
 * House rules for the copy in this file:
 *   - Gender-neutral Hebrew. Infinitives and nouns, not conjugated
 *     second-person verbs, so the app does not guess who is reading it.
 *   - No em dashes in anything a user reads.
 *   - `{0}`, `{1}` are positional placeholders filled by tr().
 */
export const he = {
  /* Identity and navigation */
  appName: 'מגביט',
  appSubtitle: 'ניהול שיעורים פרטיים',
  tagline: 'כל התלמידים, השיעורים והתשלומים במקום אחד',
  navHome: 'בית',
  navPricing: 'מחירים',
  navPremium: 'פרימיום',
  navTerms: 'תנאי שימוש',
  navPrivacy: 'פרטיות',
  navRefunds: 'מדיניות החזרים',
  navApp: 'כניסה למערכת',
  navDashboard: 'סיכום',
  navStudents: 'תלמידים',
  navLessons: 'שיעורים',
  navCalendar: 'לוח שנה',
  navPayments: 'תשלומים',
  navReceipts: 'קבלות',
  navReceiptReports: 'דוחות קבלות',
  navAdmin: 'ניהול',
  navReports: 'דוחות',
  navProfile: 'הגדרות',
  navBackup: 'גיבוי',
  navLogout: 'יציאה',
  openMenu: 'פתיחת תפריט',

  /* Shared actions */
  save: 'שמירה',
  cancel: 'ביטול',
  close: 'סגירה',
  delete: 'מחיקה',
  edit: 'עריכה',
  add: 'הוספה',
  search: 'חיפוש',
  confirm: 'אישור',
  back: 'חזרה',
  retry: 'ניסיון נוסף',
  loading: 'טוען',
  copy: 'העתקה',
  copied: 'הועתק',
  share: 'שיתוף',
  print: 'הדפסה',

  /* Lesson status */
  paid: 'שולם',
  unpaid: 'לא שולם',
  scheduled: 'מתוכנן',
  stCancelledCharged: 'בוטל בחיוב',
  stCancelledFree: 'בוטל ללא חיוב',
  stPaidDesc: 'הכסף התקבל',
  stUnpaidDesc: 'חיוב שממתין לתשלום',
  stCancelledChargedDesc: 'השיעור בוטל ועדיין מחויב',
  stCancelledFreeDesc: 'השיעור בוטל ואינו מחויב',
  stPostpone: 'דחייה למועד אחר',
  stPostponeDesc: 'העברה לתאריך חדש, וחזרה למצב לא שולם',
  statusMenuTitle: 'מצב השיעור',
  statusChangedTo: 'המצב שונה ל{0}',
  statusChangeError: 'שינוי המצב נכשל',

  /* Money and debt */
  debt: 'חוב פתוח',
  income: 'הכנסות',
  amount: 'סכום',
  price: 'מחיר לשיעור',
  currency: '₪',
  owedSince: 'בחוב מאז {0}',

  /* Students */
  studentName: 'שם התלמיד/ה',
  firstName: 'שם פרטי',
  lastName: 'שם משפחה',
  phone: 'טלפון',
  address: 'כתובת',
  entryCode: 'קוד כניסה',
  notes: 'הערות',
  archived: 'בארכיון',
  noStudents: 'אין עדיין תלמידים',
  noStudentsHint: 'אפשר להוסיף תלמיד/ה ראשון/ה כדי להתחיל',
  noStudentsMatch: 'אין תלמידים שמתאימים לסינון',
  noStudentsMatchHint: 'אפשר לאפס את הסינון כדי לראות את כל הרשימה',

  /* Roster filters */
  sortLabel: 'מיון',
  sortByName: 'שם א-ת',
  sortByLessons: 'הכי הרבה שיעורים',
  sortByIncome: 'סך הכנסה',
  sortByDebt: 'הכי הרבה חוב',
  debtFilterLabel: 'חוב',
  debtFilterAll: 'כל התלמידים',
  debtFilterWith: 'עם חוב',
  debtFilterWithout: 'ללא חוב',
  statusFilterLabel: 'סטטוס',
  statusFilterActive: 'פעילים',
  statusFilterArchived: 'בארכיון',
  statusFilterBoth: 'פעילים וארכיון',
  rosterStudents: 'תלמידים',
  rosterOwingCount: 'תלמידים חייבים',
  rosterMarkedPaid: 'סומן כשולם',
  history: 'היסטוריה',
  markAllPaid: 'סימון הכל כשולם',
  markAllPaidTitle: 'סימון החוב של {0} כשולם',
  markAllPaidBody: 'רק שיעורים שכבר התקיימו ועדיין לא שולמו יסומנו. שיעורים עתידיים לא ייגעו.',
  addManyDates: 'הוספת כמה תאריכים',
  addManyDatesSave: 'הוספת שיעור אחד || הוספת {0} שיעורים',
  addManyDatesHint: 'שעה ריקה יורשת את השעה הקבועה של התלמיד/ה באותו יום',
  addDate: 'הוספת תאריך',
  remove: 'הסרה',
  weekday: 'יום',
  durationMinutes: 'דקות',
  contactsLocked: 'אנשי קשר והודעות תשלום זמינים במסלול פרימיום',
  editorExtrasHint: 'שעות קבועות ואנשי קשר נפתחים אחרי שמירת התלמיד/ה',
  absences: 'היעדרות ידועה מראש',
  absenceStart: 'מתאריך',
  absenceEnd: 'עד תאריך',
  absenceNote: 'הערה',
  absenceNoteEn: 'הערה באנגלית',
  bankNameEn: 'שם הבנק באנגלית',
  bankOwnerEn: 'שם בעל/ת החשבון באנגלית',
  businessNameEn: 'שם העסק באנגלית',
  businessAddressEn: 'כתובת העסק באנגלית',
  signatureNameEn: 'שם החותם/ת באנגלית',
  footerTextEn: 'טקסט תחתון באנגלית',
  absenceNotePlaceholder: 'למשל: חופשה, יד שבורה',
  slotConflict: 'השעה הזו כבר קבועה אצל תלמיד/ה אחר/ת',

  /* Time */
  weekdaySunday: 'א',
  weekdayMonday: 'ב',
  weekdayTuesday: 'ג',
  weekdayWednesday: 'ד',
  weekdayThursday: 'ה',
  weekdayFriday: 'ו',
  weekdaySaturday: 'ש',
  duration: 'משך',
  minutes: 'דקות',
  time: 'שעה',
  date: 'תאריך',

  /* Premium */
  premiumTitle: 'מגביט פרימיום',
  premiumLocked: 'הפיצ׳ר הזה זמין במנוי פרימיום',
  premiumUpgrade: 'שדרוג לפרימיום',
  premiumActive: 'המנוי פעיל',
  premiumExpired: 'תוקף המנוי פג',

  /* Errors and empty states */
  errorTitle: 'משהו השתבש',
  errorGeneric: 'הפעולה נכשלה. אפשר לנסות שוב.',
  errorNetwork: 'אין חיבור לשרת',
  errorNotFound: 'העמוד לא נמצא',
  errorNotFoundHint: 'הקישור אולי השתנה או שהעמוד הוסר',
  errorUnauthorized: 'צריך להתחבר מחדש',
  emptyTitle: 'אין כאן עדיין כלום',

  /* Public documents */
  paymentRequestTitle: 'בקשת תשלום',
  paymentRequestInvalid: 'הקישור אינו תקין או שפג תוקפו',
  intakeFormTitle: 'טופס הצטרפות',
  intakeFormInvalid: 'הטופס אינו זמין',
  intakeFormThanks: 'הפרטים התקבלו. תודה!',

  /* Marketing */
  marketingHeroLead: 'מגביט מרכזת תלמידים, שיעורים, תשלומים ותזכורות במקום אחד.',
  marketingCta: 'כניסה למערכת',
  pricingTitle: 'מסלול שמתאים לקצב העסק',

  /* Sign in */
  loginTitle: 'מגביט',
  loginLead: 'התחברות עם חשבון Google כדי להגיע לתלמידים, לשיעורים ולתשלומים.',
  loginWithGoogle: 'התחברות עם Google',
  loginPrivacyMore: 'מדיניות פרטיות ',
  loginAbout: 'מה מגביט עושה',
  loginFailed: 'ההתחברות נכשלה. אפשר לנסות שוב.',

  /* Dashboard */
  scheduledIncome: 'הכנסה צפויה',
  todayTitle: 'היום',
  todayLessons: 'שיעורי היום',
  todayNone: 'אין שיעורים היום',
  nextLessonTitle: 'השיעור הבא',
  nextLessonNone: 'אין שיעור מתוכנן',
  starterTitle: 'שלושה צעדים להתחלה',
  starterAddStudent: 'הוספת תלמיד/ה ראשון/ה',
  starterAddSlot: 'קביעת שעה קבועה',
  starterAddLesson: 'רישום שיעור ראשון',
  collectedIncome: 'הכנסות שנגבו השנה',
  lessonCount: 'שיעורים',
  studentCount: 'תלמידים',
  averageLesson: 'ממוצע לשיעור',
  dashboardSummary: 'סיכום פעילות',
  dashboardAnalytics: 'ניתוח הפעילות',
  openDebtAlert: 'שיעור אחד עם חוב פתוח || {0} שיעורים עם חוב פתוח',
  openDebtHint: 'מעבר לרשימת התשלומים',
  debtStudentsTitle: 'תלמידים עם חוב פתוח',
  debtLessonCount: 'שיעור אחד שטרם שולם || {0} שיעורים שטרם שולמו',
  viewAllPayments: 'לכל התשלומים',
  paymentStatusChart: 'סטטוס תשלומים',
  paidDebtScheduled: 'שולם · חוב · עתידי',
  monthlyIncomeChart: 'הכנסה חודשית',
  lastSixMonths: 'ששת החודשים האחרונים',
  topIncomeStudents: 'מובילים לפי הכנסה',
  topFive: 'חמשת המובילים',
  lessonsByStudent: 'שיעורים לפי תלמיד',
  navPaymentDocuments: 'מסמכי תשלום',

  /* Students */
  addStudent: 'הוספת תלמיד/ה',
  editStudentTitle: 'עריכת כרטיס',
  showArchived: 'הצגת ארכיון',
  openStudent: 'כרטיס מלא',
  deleteStudentTitle: 'מחיקת {0}',
  deleteStudentBody: 'המחיקה מוחקת גם את כל השיעורים, השעות הקבועות, אנשי הקשר והזמינות של התלמיד/ה. אי אפשר לשחזר.',

  /* Lessons */
  addLesson: 'הוספת שיעור',
  filterAll: 'הכול',
  noLessons: 'אין שיעורים להצגה',
  noLessonsHint: 'אפשר להוסיף שיעור או לשנות את הסינון',
  paymentsSearchPlaceholder: 'חיפוש לפי שם תלמיד',
  paymentsFilterLabel: 'סינון תשלומים',
  allStatuses: 'כל הסטטוסים',
  resetFilters: 'איפוס',
  printList: 'הדפסת רשימה',
  paymentsShowing: 'מציג תשלום אחד || מציג {0} תשלומים',
  pageStatus: 'עמוד {0} מתוך {1}',
  previousPage: 'העמוד הקודם',
  nextPage: 'העמוד הבא',
  editPaymentStatus: 'שינוי מצב התשלום',
  cancelledStatuses: 'ביטולים',
  postponeTitle: 'דחיית השיעור של {0}',
  postponeHint: 'בחירת תאריך ושעה חדשים. מצב השיעור חוזר ללא שולם.',
  editLesson: 'עריכת שיעור',
  editLessonDesc: 'שינוי תלמיד, תאריך, שעה או סכום',
  deleteLessonDesc: 'הסרת השיעור מהרישום לגמרי',
  clearAllLessons: 'ניקוי הכל',
  clearAllLessonsTitle: 'מחיקת כל השיעורים',
  clearAllLessonsBody: 'כל השיעורים יימחקו, גם אלה שאינם מוצגים בסינון הנוכחי. רישום ההכנסות יתאפס. אי אפשר לשחזר.',
  deleteLessonTitle: 'מחיקת שיעור של {0}',
  deleteLessonBody: 'השיעור יימחק מהרישום ולא ייספר בהכנסות או בחוב. ביטול שיעור נשאר ברישום, מחיקה לא. אי אפשר לשחזר.',

  /* Calendar */
  previousMonth: 'החודש הקודם',
  nextMonth: 'החודש הבא',
  calendarToday: 'היום',
  calendarMonth: 'חודש',
  calendarWeek: 'שבוע',
  calendarDay: 'יום',
  calendarView: 'תצוגת יומן',
  calendarPrevious: 'התקופה הקודמת',
  calendarNext: 'התקופה הבאה',
  calendarNoDayLessons: 'אין שיעורים ביום זה',
  calendarNoLessonsShort: 'אין שיעורים',
  calendarDayLoad: 'שיעור אחד || {0} שיעורים',
  calendarNoTime: 'ללא שעה',
  calendarPlanned: 'שעה קבועה',

  /* Settings */
  accountTitle: 'החשבון',
  displayTitle: 'תצוגה',
  languageLabel: 'שפה',
  themeLabel: 'ערכת נושא',
  themeLight: 'בהיר',
  themeDark: 'כהה',
  paymentDetailsTitle: 'פרטי תשלום',
  payboxLink: 'קישור PayBox',
  bitLink: 'קישור ביט',
  bankName: 'בנק',
  bankBranch: 'סניף',
  bankAccount: 'מספר חשבון',
  bankOwner: 'שם בעל החשבון',
  copyBankDetails: 'העתקת פרטי הבנק',
  sincerely: 'בברכה,',
  issuedOn: 'הופק בתאריך',
  savedNotice: 'נשמר',

  /* Premium */
  premiumAdminAccess: 'הגישה ניתנת דרך הרשאת ניהול',
  premiumUntil: 'בתוקף עד',
  premiumManageBilling: 'ניהול התשלום והחיוב',
  premiumFeaturesTitle: 'מה נכלל',
  featureExports: 'ייצוא לאקסל ול-CSV',
  featureBranding: 'מיתוג העסק על הקבלות',
  featureReminders: 'תזכורות תשלום ואנשי קשר',
  featureStudentProfile: 'כרטיס תלמיד/ה מלא',
  featureIntakeForms: 'טופס הצטרפות ללקוחות',

  /* Receipts */
  receiptTitle: 'קבלה',
  receiptDraft: 'טיוטה',
  receiptNumber: 'מספר קבלה',
  receiptFor: 'לכבוד',
  total: 'סך הכול',
  issueReceipt: 'הפקת קבלה',
  cancelReceipt: 'ביטול קבלה',
  cancelReceiptTitle: 'ביטול קבלה {0}',
  cancelReceiptBody: 'קבלה שבוטלה נשארת ברישום, והמספר שלה לא חוזר לשימוש.',
  cancelReceiptReason: 'סיבת הביטול',
  receiptCancelled: 'הקבלה בוטלה.',
  noReceipts: 'אין עדיין קבלות',
  noReceiptsHint: 'קבלה נוצרת משיעורים ששולמו',
  unreportedTitle: 'שיעורים ששולמו וממתינים לקבלה',
  writeReceipt: 'הפקת קבלה',
  bulkIssueAction: 'הפקת קבלות לכל השיעורים',
  bulkIssueTitle: 'הפקת קבלה אחת || הפקת {0} קבלות',
  bulkIssueBody: 'קבלה נפרדת לכל שיעור, במספור רץ לפי סדר השיעורים הכרונולוגי. כל מסמך מתוארך להיום, שהוא יום ההפקה, ויום השיעור נרשם עליו כתאריך קבלת הכסף. אי אפשר לבטל את כל הפעולה בבת אחת; כל קבלה אפשר לבטל בנפרד לאחר מכן.',
  bulkIssueConfirm: 'הפקת קבלות',
  bulkIssueDone: 'קבלה אחת הופקה בהצלחה. || {0} קבלות הופקו בהצלחה.',
  newReceipt: 'קבלה חדשה',
  editReceipt: 'עריכת טיוטה',
  duplicate: 'שכפול',
  duplicateReceipt: 'שכפול קבלה',
  receiptIssued: 'הופקה',
  receiptCancelledShort: 'מבוטלת',
  receiptsShowing: 'מציג קבלה אחת || מציג {0} קבלות',
  receiptSearchPlaceholder: 'חיפוש לפי שם, מספר או תיאור',
  noReceiptsMatch: 'אין קבלות שמתאימות לסינון',
  noReceiptsMatchHint: 'אפשר לאפס את הסינון כדי לראות את כל הרשימה',
  deleteDraftTitle: 'מחיקת טיוטה',
  deleteDraftBody: 'לטיוטה אין מספר קבלה, ומחיקתה לא משאירה חור במספור. אי אפשר לשחזר.',
  oneOffCustomer: 'לקוח חד-פעמי',
  customerName: 'שם הלקוח',
  receiptDescription: 'תיאור',
  issueDate: 'תאריך הפקה',
  paymentDate: 'תאריך תשלום',
  paymentMethod: 'אמצעי תשלום',
  paymentReference: 'אסמכתה',
  allMethods: 'כל אמצעי התשלום',
  fromDate: 'מתאריך',
  toDate: 'עד תאריך',
  minAmount: 'מסכום',
  maxAmount: 'עד סכום',
  status: 'סטטוס',
  methodCash: 'מזומן',
  methodBankTransfer: 'העברה בנקאית',
  methodCreditCard: 'כרטיס אשראי',
  methodBit: 'ביט',
  methodPaybox: 'פייבוקס',
  methodCheck: "צ'ק",
  methodOther: 'אחר',

  /* Israeli compliance: what the law puts on the face of a document */
  docTypeReceipt: 'קבלה',
  docTypeTaxInvoiceReceipt: 'חשבונית מס/קבלה',
  docOriginal: 'מקור',
  docCopy: 'העתק נאמן למקור',
  docComputerGenerated: 'מסמך ממוחשב',
  docSoftwareRegistry: 'מספר רישום תוכנה {0}',
  docVatExemptLabel: 'עוסק פטור',
  docVatRegisteredLabel: 'עוסק מורשה',
  docCompanyLabel: 'ח.פ.',
  docAmutaLabel: 'מספר עמותה',
  docCancelledStamp: 'מבוטל',
  receiptPaymentDetails: 'פרטי התשלום',
  receiptNetAmount: 'סכום לפני מע״מ',
  receiptVatLine: 'מע״מ {0}%',
  receiptGrossAmount: 'סך הכול לתשלום',
  chequeLine: 'בנק {0}, סניף {1}, חשבון {2}, צ׳ק {3}',
  chequeDueDate: 'לפירעון ב-{0}',
  cardEnding: 'מסתיים ב-{0}',
  receiptIssuedBy: 'הופק על ידי',
  customerAddress: 'כתובת הלקוח',
  customerTaxId: 'ח.פ. / ת.ז. של הלקוח',
  paymentsTitle: 'פירוט התשלום',
  paymentsNote: 'קבלה חייבת לפרט איך הכסף התקבל. לצ׳ק נדרשים בנק, סניף, חשבון, מספר ותאריך פירעון.',
  addPayment: 'הוספת אמצעי תשלום',
  removePayment: 'הסרה',
  bankNumber: 'בנק',
  branchNumber: 'סניף',
  accountNumber: 'חשבון',
  chequeNumber: 'מספר צ׳ק',
  chequeDueDateField: 'תאריך פירעון',
  cardBrand: 'חברת האשראי',
  cardLast4: '4 ספרות אחרונות',
  paymentAmount: 'סכום',
  paymentsBalanceHint: 'סכום אמצעי התשלום חייב להיות שווה לסכום הקבלה.',
  issueDateIsToday: 'מסמך מתוארך ליום ההפקה. תאריך קבלת הכסף נרשם בנפרד.',

  /* Israeli compliance: business settings */
  businessTypeLabel: 'סוג העסק',
  businessTypeOsekPatur: 'עוסק פטור',
  businessTypeOsekMurshe: 'עוסק מורשה',
  businessTypeCompany: 'חברה בע״מ',
  businessTypeAmuta: 'עמותה',
  businessTypeNote: 'עוסק פטור מפיק קבלה ואינו גובה מע״מ. עוסק מורשה וחברה מפיקים חשבונית מס/קבלה, והמע״מ מחושב מתוך הסכום שהתלמיד משלם.',
  registrationNumberHelp: 'תשע ספרות, כולל ספרת ביקורת. מופיע על כל מסמך.',
  addressStreet: 'רחוב',
  addressHouse: 'מספר בית',
  addressCity: 'עיר',
  addressZip: 'מיקוד',
  addressPartsNote: 'הכתובת מופיעה על המסמך, ונדרשת בנפרד לרחוב, בית ועיר בייצוא לרשות המסים.',
  softwareRegistryNumber: 'מספר רישום תוכנה',
  softwareRegistryHelp: 'המספר שרשות המסים מנפיקה בעת רישום התוכנה במאגר התוכנות. מופיע על כל מסמך ובקובץ המבנה האחיד.',
  numberingLockedNote: 'כבר הופק מסמך ממוספר, ולכן אי אפשר לשנות את המספור. רצף מספרים חייב להיות אחיד ורציף לאורך שנת המס.',
  complianceTitle: 'מוכנות לחוק',
  complianceReady: 'כל הפרטים הנדרשים מולאו. המסמכים שיופקו עומדים בדרישות ניהול הספרים.',
  complianceMissing: 'חסרים פרטים שהחוק דורש על כל מסמך:',
  complianceWillIssue: 'המסמך שיופק: {0}',
  complianceVatRate: 'שיעור מע״מ נוכחי: {0}%',
  complianceFieldBusinessName: 'שם העסק',
  complianceFieldRegistrationNumber: 'מספר עוסק',
  complianceFieldAddress: 'כתובת העסק',
  complianceFieldSoftwareRegistryNumber: 'מספר רישום תוכנה',

  /* Israeli compliance: the uniform-structure export */
  openFormatTitle: 'ממשק פתוח',
  openFormatBody: 'הפקת קבצים במבנה אחיד לרשות המסים: INI.TXT ו-BKMVDATA.TXT לתקופה שנבחרה. זה הקובץ שמבקרת מס מבקשת.',
  openFormatMoved: 'ממשק פתוח נמצא במסך משלו, צעד אחד מהתפריט הראשי.',
  openFormatOpen: 'מעבר לממשק פתוח',
  openFormatActivate: 'הפעלת ממשק פתוח',
  openFormatRunAgain: 'הפקה נוספת',
  openFormatDialogBusiness: 'בית העסק הנבדק',
  openFormatDialogRange: 'טווח תאריכים',
  openFormatDialogDestination: 'יעד השמירה',
  openFormatDialogDestinationBody: 'הקבצים נשמרים בתיקיית ההורדות של הדפדפן, בארכיון בשם OPENFRMT.zip. דפדפן אינו חושף את הכוננים שבמחשב, ולכן אין כאן בחירת כונן. בתוך הארכיון הקבצים מסודרים במבנה התיקיות שההוראות דורשות:',
  openFormatDialogRun: 'הפקת הקבצים',
  openFormatRunning: 'בהפקה...',
  openFormatRunVatId: 'מספר עוסק מורשה',
  openFormatRunBusiness: 'שם בית העסק',
  openFormatRunDone: 'ביצוע ממשק פתוח הסתיים בהצלחה.',
  openFormatRunPath: 'הנתונים נשמרו בנתיב הבא:',
  openFormatRunPathNote: 'בתוך הארכיון OPENFRMT.zip, בתיקיית ההורדות של הדפדפן.',
  openFormatRunRange: 'טווח תאריכים: מתאריך {0} ועד תאריך {1}',
  openFormatRunRecordsTitle: 'פירוט סך סוגי הרשומות שנוצרו בקובץ BKMVDATA.TXT',
  openFormatRunCode: 'קוד רשומה',
  openFormatRunDescription: 'תיאור רשומה',
  openFormatRunCount: 'סך רשומות',
  openFormatRecordA100: 'רשומה פתיחה',
  openFormatRecordB100: 'תנועות בהנה״ח',
  openFormatRecordB110: 'חשבון בהנה״ח',
  openFormatRecordC100: 'כותרת מסמך',
  openFormatRecordD110: 'פרטי מסמך',
  openFormatRecordD120: 'פרטי קבלות',
  openFormatRecordM100: 'פריט במלאי',
  openFormatRecordZ900: 'רשומת סיום',
  openFormatRunSoftware: 'הנתונים הופקו באמצעות תוכנת {0}, מספר תעודת הרישום {1}, בתאריך {2} בשעה {3}',
  openFormatRunNoRegistry: 'טרם הונפק',
  openFormatRangeError: 'יש לבחור טווח תאריכים תקין.',
  openFormatSummaryTitle: 'דוח כמויות מסמכים',
  openFormatSummaryBody: 'כמות וסכום לכל סוג מסמך בנספח 1 של הוראות המבנה האחיד, לאותה תקופה. סוג שאינו מנוהל בתוכנה מופיע באפס. זה הדוח שמבקר מס מחזיק מול הקובץ.',
  openFormatSummaryShow: 'הצגת הדוח',
  openFormatSummaryPrint: 'הדפסה',
  openFormatSummaryCode: 'קוד',
  openFormatSummaryDocument: 'סוג המסמך',
  openFormatSummaryCount: 'כמות',
  openFormatSummaryTotal: 'סכום',
  openFormatSummaryCancelled: 'מתוכם מבוטלים',
  openFormatSummaryRecords: 'רשומות בקובץ',
  openFormatSummaryPeriod: 'תקופה',

  /* Israeli compliance: what went wrong */
  errorNumberingLocked: 'אי אפשר לשנות מספור אחרי שהופק מסמך ממוספר.',
  errorRegistrationNumberRequired: 'עוסק מורשה או חברה חייבים מספר עוסק לפני הפקת מסמך.',
  errorInvalidRegistrationNumber: 'מספר העוסק אינו תקין. יש לבדוק את ספרת הביקורת.',
  errorInvalidCustomerTaxId: 'מספר הזיהוי של הלקוח אינו תקין.',
  errorPaymentsDoNotBalance: 'סכום אמצעי התשלום אינו שווה לסכום הקבלה.',
  errorPaymentDetailsRequired: 'חסרים פרטי תשלום שהחוק דורש.',

  /* Business settings */
  businessTitle: 'פרטי עסק',
  businessSnapshotNote: 'שינוי כאן לא משנה קבלות שכבר הופקו. בהפקה נשמרת תמונת מצב של הפרטים, כדי שמסמך רשמי לא ישתנה בדיעבד.',
  businessDetails: 'פרטי העסק',
  businessName: 'שם העסק',
  registrationNumber: 'מספר עוסק',
  email: 'דואר אלקטרוני',
  website: 'אתר',
  numberingTitle: 'מספור קבלות',
  numberPrefix: 'קידומת',
  numberStart: 'מספר התחלה',
  numberLength: 'אורך המספר',
  yearPrefix: 'הוספת שנה למספר',
  perYearNumbering: 'מונה נפרד לכל שנת מס',
  nextNumberPreview: 'המספר הבא ייראה כך:',
  documentTitle: 'המסמך',
  signatureName: 'שם לחתימה',
  printSize: 'גודל הדפסה',
  printSizeReceipt: 'סרט קבלה',
  footerText: 'טקסט תחתון',
  saved: 'נשמר',

  /* Receipt reports */
  noIssuedReceipts: 'עוד לא הופקו קבלות',
  noIssuedReceiptsHint: 'הדוחות מתמלאים אחרי הפקת הקבלה הראשונה',
  receiptsCount: 'כמות',
  issuedCount: 'קבלות שהופקו',
  issuedTotal: 'סך שהופק',
  averageReceipt: 'ממוצע לקבלה',
  sequenceTitle: 'רצף המספור',
  sequenceComplete: 'הרצף שלם, אין מספרים חסרים.',
  sequenceGaps: 'חסרים המספרים:',
  byMonth: 'לפי חודש',
  byStudent: 'לפי לקוח',
  byMethod: 'לפי אמצעי תשלום',

  /* Admin */
  adminOnly: 'המסך הזה מיועד למנהלי המערכת',
  adminOnlyHint: 'אין לך הרשאת ניהול בחשבון הזה',
  adminSearchPlaceholder: 'חיפוש לפי מזהה, דואר אלקטרוני או שם',
  adminNoUsers: 'לא נמצאו משתמשים',
  adminNoUsersHint: 'אפשר לחפש לפי מזהה מלא, או לפי חלק מהדואר האלקטרוני',
  adminMonths: 'מספר חודשי פרימיום',
  adminMonthsHint: 'הענקה וחידוש מוסיפים את מספר החודשים לסוף התקופה הנוכחית, או מהיום אם אין תקופה פעילה. שדה חודשים ריק: התוקף נקבע לפי התאריך, ובלי תאריך אין תאריך סיום.',
  adminExpiresAt: 'בתוקף עד',
  adminNote: 'הערה',
  adminGrant: 'הענקת פרימיום',
  adminTrial: 'התחלת תקופת ניסיון',
  adminCancel: 'ביטול מנוי',
  adminRevoke: 'שלילת פרימיום',
  adminExpire: 'סימון כפג תוקף',
  adminRenew: 'חידוש',
  adminNotes: 'שמירת הערה',
  adminAudit: 'יומן פעולות',
  adminNoAudit: 'אין עדיין רישומים',
  adminGrantedManually: 'הוענק ידנית',

  /* Monthly report */
  reportMonth: 'חודש',
  reportBilled: 'חויב',
  reportSummaryTitle: 'סיכום החודש',
  reportLessons: 'שיעורים',
  reportCollectionRate: 'שיעור הגבייה',
  reportActiveStudents: 'תלמידים פעילים',
  reportVsPrevious: 'לעומת החודש הקודם',
  reportMissingReceipts: 'הכנסות ללא קבלה',
  reportByStudent: 'לפי תלמיד/ה',
  reportByWeekday: 'לפי יום בשבוע',
  reportInactive: 'לא היו שיעורים החודש',
  noReports: 'אין עדיין דוחות',
  noReportsHint: 'דוח נוצר בסוף כל חודש שיש בו שיעורים',

  /* Student page */
  rhythmTitle: 'קצב',
  lessonsDone: 'שיעורים שהתקיימו',
  attendanceRate: 'שיעור הגעה',
  daysSinceLast: 'ימים מהשיעור האחרון',
  lessonsPerMonth: 'שיעורים בחודש',
  freeCancels: 'ביטולים ללא חיוב',
  chargedCancels: 'ביטולים בחיוב',
  monthlyTrend: 'לאורך השנה',
  recurringHours: 'שעות קבועות',
  availabilityTitle: 'זמינות',
  contactsTitle: 'אנשי קשר',

  /* Public documents */
  howToPay: 'איך משלמים',
  lessonsInThisRequest: 'השיעורים בבקשה',
  from: 'משעה',
  until: 'עד שעה',
  contactName: 'שם איש קשר',
  contactPhone: 'טלפון איש קשר',
  send: 'שליחה',

  /* Reminders and intake links */
  navReminders: 'תזכורות תשלום',
  navIntakeForms: 'טופסי הצטרפות',
  sendReminder: 'שליחת תזכורת',
  sendReminderShort: 'שליחה',
  sendReminderWhatsApp: 'שליחת תזכורת בוואטסאפ',
  copyReminderShort: 'העתקה',
  copyReminderNoPhone: 'העתקת הודעת תזכורת, אין מספר טלפון בכרטיס',
  reminderCopied: 'הועתק',
  reminderLockedTitle: 'תזכורות תשלום זמינות במסלול פרימיום',
  chooseReminderRecipient: 'שליחת תזכורת',
  chooseReminderRecipientHint: 'בחירת איש הקשר לקבלת הודעת התשלום',
  copyReminderPrompt: 'העתקת הודעת התזכורת:',
  reminderHello: 'שלום {0},',
  reminderIntro: 'מצורף ריכוז השיעורים שטרם שולמו:',
  reminderIntroFor: 'מצורף ריכוז השיעורים של {0} שטרם שולמו:',
  reminderLessonLine: '{0} · {1} · {2} ₪ {3}',
  reminderCancelledNote: '(בוטל בתשלום)',
  reminderTotal: 'סה״כ לתשלום: {0} ₪',
  reminderDocumentIntro: 'לצפייה בבקשת התשלום:',
  reminderHowToPay: 'אפשרויות תשלום',
  paybox: 'PayBox',
  bit: 'ביט',
  bankTransfer: 'העברה בנקאית',
  accountOwner: 'שם בעל החשבון',
  reminderThanks: 'תודה רבה',
  reminderSignature: 'נשלח באמצעות מגביט',
  reminderGreeting: 'שלום, ריכוז התשלומים עבור {0}:',
  noDebt: 'אין חובות פתוחים',
  noDebtHint: 'שיעור נחשב חוב רק אחרי שהסתיים',
  noPhone: 'אין מספר טלפון שמור',
  newIntakeForm: 'יצירת קישור',
  noIntakeForms: 'אין עדיין קישורים',
  noIntakeFormsHint: 'קישור נשלח ללקוח כדי שימלא את הפרטים בעצמו',
  formOpen: 'פעיל',
  formClosed: 'סגור',
  revokeForm: 'סגירת הקישור',
  revokeFormBody: 'הקישור יפסיק לעבוד. הכרטיסים שכבר התקבלו דרכו נשארים.',
  create: 'יצירה',
  undo: 'ביטול הפעולה',
  lessonDeleted: 'השיעור של {0} נמחק',
  markedPaidCount: 'שיעור אחד סומן כשולם || {0} שיעורים סומנו כשולמו',
  studentQuietWarning: 'עבר יום אחד מהשיעור האחרון ואין שיעור מתוכנן || עברו {0} ימים מהשיעור האחרון ואין שיעור מתוכנן',
  lessonsByWeekday: 'שיעורים לפי יום בשבוע',
  fromHour: 'משעה',
  toHour: 'עד שעה',
  checkoutFailed: 'לא הצלחנו לפתוח את התשלום. אפשר לנסות שוב.',
  shareOnWhatsApp: 'שיתוף בוואטסאפ',
  intakeShareMessage: 'אפשר למלא כאן את פרטי ההצטרפות:',
  intakeLabel: 'שם לקישור',
  intakeLabelHint: 'רק בשבילך, כדי להבדיל בין קישורים',
  intakeNote: 'הערה למי שממלא',
  intakeNoteHint: 'מוצגת בראש הטופס למי שפותח את הקישור',
  formSubmissions: 'כרטיס אחד התקבל || {0} כרטיסים התקבלו',
  intakeReceivedTitle: 'כרטיסים שהתקבלו',
  intakeReceivedNone: 'עוד לא התקבלו כרטיסים דרך הקישורים',

  /* Landing page */
  heroTitle: 'מגביט',
  heroSub: 'כמו מנהלת חשבונות ומזכירה אישית בלי חפירות של AI.',
  ctaButton: 'כניסה עם Google',
  moneyTitle: 'מי חייב לך, מה נכנס, ומה בוטל.',
  moneyCopy: 'כל שיעור עם סטטוס משלו: שולם, פתוח, בוטל ומחויב, או בוטל בלי חיוב. בכרטיס תלמיד: כמה הוא חייב. בתמונה החודשית: כמה נכנס, ומי מביא הכי הרבה.',
  moneyCaption: 'רשימת תלמידים עם עמודת חוב פתוח.',
  moneyShotAlt: 'רשימת תלמידים באפליקציית מגביט עם עמודת חוב פתוח לכל תלמיד',
  syncTitle: 'השיעורים שלך כבר ביומן Google.',
  syncRule1: 'אם יש סתירה בין מגביט ליומן, היומן תמיד מנצח.',
  syncRule2: 'סנכרון אוטומטי אף פעם לא מוחק שיעורים לבד.',
  syncRule3: 'שינוי, ביטול או מחיקה במגביט מתעדכנים ביומן אוטומטית.',
  seeItTitle: 'ככה זה נראה.',
  seeItCaption: 'לוח השיעורים השבועי, בזמן אמת.',
  seeItShotAlt: 'לוח השיעורים השבועי באפליקציית מגביט, עם שיעורים מסומנים כשולם, לא שולם ובוטל מחויב',
  premiumEyebrow: 'מגביט Premium',
  pmTitle: 'קבלות, בלי לזכור כלום.',
  pmFeature1Title: 'קבלה דיגיטלית, בלי לזכור',
  pmFeature1Text: 'קבלה דיגיטלית, עם מספור רציף, נוצרת אוטומטית לכל שיעור ששולם. אם יש שיעור ששולם ועדיין בלי קבלה, יש התראה, כדי שלא תישאר הכנסה בלי תיעוד.',
  pmFeature2Title: 'דוחות וייצוא',
  pmFeature2Text: 'דוחות הכנסה חודשיים, וייצוא ל-CSV ול-Excel.',
  premiumCtaTrial: 'התחלת 7 ימי ניסיון',
  premiumCtaPricing: 'לכל פרטי המחירים',
  faqEyebrow: 'שאלות נפוצות',
  faqTitle: 'לפני שמתחילים.',
  faq1Q: 'צריך כרטיס אשראי כדי להתחיל?',
  faq1A: 'לא, לא בהרשמה. כרטיס נדרש רק בתחילת ניסיון הפרימיום, בלי חיוב עד תום 7 הימים, עם אפשרות ביטול לפני כן בלי תשלום.',
  faq2Q: 'איך עובד הסנכרון עם Google Calendar?',
  faq2A: 'דו-כיווני: שיעורים נכנסים ליומן, ושינויים ביומן חוזרים ל-מגביט. בהתנגשות, היומן מנצח. מחיקה אוטומטית לא קיימת, היא קורית רק בסנכרון ידני, עם אישור מראש.',
  faq3Q: 'הנתונים בטוחים, ואפשר לייצא אותם?',
  faq3A: 'כן. ההתחברות דרך Google מאובטחת, וכל תלמיד ותשלום שייכים רק לחשבון המחובר, מופרדים לגמרי ממשתמשים אחרים. ייצוא מלא של הנתונים אפשרי בכל רגע, כולל דוחות הכנסה ב-CSV ו-Excel במסלול הפרימיום.',
  faq4Q: 'מה קורה בביטול הפרימיום?',
  faq4A: 'ביטול אפשרי בכל רגע מעמוד המנוי, עם גישה עד סוף התקופה ששולמה. ועל החיוב הראשון יש החזר מלא תוך 14 יום.',
  ctaTitle: 'להתחיל בלי כרטיס אשראי.',
  ctaCopy: 'כניסה עם Google, תלמיד ראשון תוך דקה.',

  /* Pricing */
  pricingHeadline: 'פחות ניהול. יותר זמן ללמד.',
  pricingLede: 'ניהול התלמידים, השיעורים והתשלומים מתחיל בחינם. כשהעסק צריך קבלות, תזכורות ודוחות שעובדים לבד, פרימיום מטפל גם בזה. כולל {0} ימי ניסיון ללא חיוב.',
  pricingFlowLabel: 'משיעור ועד קבלה',
  pricingFlowLesson: 'שיעור',
  pricingFlowPayment: 'תשלום',
  pricingFlowReceipt: 'קבלה',
  planFreeKicker: 'לניהול השוטף',
  planFreeName: 'חינמי',
  planFreePrice: 'חינם',
  planForever: 'לתמיד',
  planFreeSub: 'כל מה שצריך כדי לנהל שיעורים ותשלומים.',
  planFreeCta: 'התחלה בחינם',
  planFree1: 'ניהול תלמידים ומחירים ללא הגבלה',
  planFree2: 'רישום שיעורים, תשלומים וחובות',
  planFree3: 'לוח שנה חודשי ושבועי',
  planFree4: 'סנכרון דו-כיווני עם Google Calendar',
  planFree5: 'גרפים ותובנות הכנסה',
  pricingPremiumBadge: 'הבחירה לעסק פעיל',
  planPremiumSub: 'העבודה המנהלית נסגרת בזמן, גם כשהיום מלא בשיעורים.',
  planPremium1: 'כל מה שנכלל במסלול החינמי',
  planPremium2: 'קבלות דיגיטליות אוטומטיות עם מספור רציף',
  planPremium3: 'קבלות מרוכזות ומסמכים מוכנים להדפסה',
  planPremium4: 'תזכורות תשלום ובקשות תשלום ל-WhatsApp',
  planPremium5: 'התראות על הכנסות שעדיין מחכות לקבלה',
  planPremium6: 'דוחות הכנסה חודשיים וייצוא ל-CSV ול-Excel',
  planPremium7: 'כרטיסי תלמידים מלאים וטופסי הצטרפות ללקוחות',
  planPremium8: 'מיתוג העסק על המסמכים וגיבוי מלא של הנתונים',
  pricingNotesTitle: 'בלי הפתעות בחיוב',
  pricingNote1: 'המחירים כוללים מע"מ. החיוב מתבצע דרך Paddle.',
  pricingNote2: 'הניסיון מתחיל בלי חיוב, וניתן לביטול לפני סופו בלי תשלום.',
  pricingNote3: 'על החיוב הראשון יש החזר מלא תוך 14 יום.',
  pricingBillingLabel: 'תדירות חיוב',
  pricingBillingMonthly: 'חודשי',
  pricingBillingYearly: 'שנתי',
  pricingAnnualSaving: 'כמעט חודשיים חיסכון',
  pricingPerMonth: 'לחודש',
  pricingPerYear: 'לשנה',
  pricingAlternatePrefix: 'או',
  pricingSubscribeCta: 'התחלת ניסיון חינם',
  pricingTrialNote: '7 ימים ללא חיוב. אפשר לבטל בכל עת.',
  pricingCheckoutUnavailable: 'ההצטרפות המקוונת אינה זמינה כרגע. המחירים והתכונות נשארים כאן לעיון.',
  welcomeTitle: 'הכל מוכן.',
  welcomeLede: 'תודה על ההצטרפות. הקבלה בדרך במייל, ומסלול הפרימיום נדלק תוך כמה דקות.',
  welcomeNote: 'מסך פרימיום שנראה נעול בהגעה אליו נפתח לאחר רענון; אישורי החיוב מגיעים ברקע ועשויים לקחת כמה דקות.',
  welcomeCta: 'מעבר לאפליקציה',

  /* Backup and contacts */
  navContacts: 'אנשי קשר',
  exportTitle: 'ייצוא',
  exportHint: 'הנתונים שלך, בקובץ שאפשר לשמור בכל מקום.',
  exportAll: 'ייצוא הכול',
  exportCards: 'ייצוא כרטיסי תלמידים',
  importTitle: 'ייבוא',
  importHint: 'ייבוא גיבוי מלא או כרטיסי תלמידים מקובץ שיוצא מ-מגביט.',
  importMerge: 'הוספה בלבד',
  importReplace: 'התאמה מלאה לקובץ',
  importMergeBody: 'יתווסף כרטיס אחד. כרטיס קיים לא ישתנה. || יתווספו {0} כרטיסים. כרטיס קיים לא ישתנה.',
  importReplaceBody: 'הפעולה תמחק {0} תלמידים ואיתם {1} שיעורים. אי אפשר לשחזר.',
  importDone: 'נוספו {0}, עודכנו {1}, נמחקו {2}',
  fullImportBody: 'הקובץ כולל {0} תלמידים ו-{1} שיעורים. הייבוא יחליף את כל הנתונים הקיימים: {2} תלמידים ו-{3} שיעורים. הפעולה אינה ניתנת לביטול.',
  fullImportDone: 'הגיבוי שוחזר: {0} תלמידים ו-{1} שיעורים',
  noContacts: 'אין אנשי קשר',
  addContact: 'הוספת איש קשר',
  relation: 'קרבה',
  receivesPayments: 'מקבל/ת הודעות תשלום',

  /* Google Calendar */
  navGoogleCalendar: 'יומן Google',
  gcalConnection: 'חיבור',
  gcalConnected: 'מחובר',
  gcalNotConnected: 'היומן עדיין לא מחובר.',
  gcalConnect: 'חיבור ליומן Google',
  gcalDisconnect: 'ניתוק',
  gcalSyncTitle: 'סנכרון',
  gcalPush: 'העלאת שיעור אחד ליומן || העלאת {0} שיעורים ליומן',
  gcalPull: 'משיכת שינויים מהיומן',
  gcalSyncDone: 'עודכן שיעור אחד || עודכנו {0} שיעורים',
  gcalMissing: 'שיעור אחד לא נמצא ביומן ונשאר כמו שהוא || {0} שיעורים לא נמצאו ביומן ונשארו כמו שהם',
  importFromGoogle: 'ייבוא מ-Google',
  importSelected: 'ייבוא {0}',
  noGoogleContacts: 'לא נמצאו אנשי קשר',
  /* Ensembles: groups that learn together */
  navEnsembles: 'הרכבים',
  addEnsemble: 'הוספת הרכב',
  editEnsembleTitle: 'עריכת הרכב',
  noEnsembles: 'אין עדיין הרכבים',
  noEnsemblesHint: 'הרכב הוא קבוצה שלומדת יחד: תזמורת, הרכב קאמרי או שיעור תיאוריה. מפגש אחד נרשם כשיעור לכל משתתף שמחויב.',
  noEnsemblesMatch: 'אין הרכב שמתאים לחיפוש',
  ensembleName: 'שם ההרכב',
  ensembleNameEn: 'שם ההרכב באנגלית',
  ensembleMembers: 'משתתפים',
  ensembleMemberCount: 'משתתף/ת אחד/ת || {0} משתתפים',
  ensembleNoMembers: 'עדיין בלי משתתפים',
  ensembleWeeklyHour: 'שעה שבועית',
  ensembleDuration: 'משך מפגש בדקות',
  billingLabel: 'תמחור',
  billingPerStudent: 'כל משתתף לפי המחיר שלו',
  billingSplit: 'מחיר קבוצתי מחולק שווה בשווה',
  billingSinglePayer: 'משתתף אחד משלם על כל הקבוצה',
  billingInstitution: 'מוסד או ארגון משלם',
  billingInstitutionHint: 'המוסד מחויב על המפגש ואף אחד מהמשתתפים לא מחויב.',
  groupPrice: 'מחיר לקבוצה',
  payerLabel: 'המשלם',
  payingOrganization: 'הארגון המשלם',
  selectPlaceholder: 'לבחירה',
  noOrganizations: 'אין עדיין ארגונים. אפשר להוסיף ארגון ממסך התלמידים.',
  scheduleMeeting: 'קביעת מפגש',
  meetingCharges: 'החיוב במפגש',
  meetingBooked: 'נקבע שיעור אחד || נקבעו {0} שיעורים',
  meetingUnbilled: 'משתתף/ת אחד/ת ללא חיוב במפגש הזה || {0} משתתפים ללא חיוב במפגש הזה',
  meetingNobody: 'אין למי לחייב את המפגש',
  deleteEnsembleTitle: 'מחיקת {0}',
  deleteEnsembleBody: 'ההרכב יימחק. השיעורים שכבר נרשמו נשארים אצל התלמידים, וזו ההכנסה שכבר נרשמה.',
  groupLesson: 'שיעור קבוצתי',
  ensembleOf: 'הרכב: {0}',

  /* A second spelling, and who is taught versus who pays */
  firstNameEn: 'שם פרטי באנגלית',
  lastNameEn: 'שם משפחה באנגלית',
  addressEn: 'כתובת באנגלית',
  notesEn: 'הערות באנגלית',
  contactNameEn: 'שם איש קשר באנגלית',
  relationEn: 'קרבה באנגלית',
  spellingHint: 'הכתיב באנגלית מוצג כשהממשק באנגלית. השם המקורי נשאר מזהה הכרטיס.',
  kindLabel: 'סוג כרטיס',
  kindStudent: 'תלמיד/ה',
  kindOrganization: 'ארגון או מוסד',
  kindOrganizationHint: 'ארגון משלם על הרכב בלי שמלמדים אותו. הוא לא נספר בין התלמידים, אבל יש לו חוב, תזכורות וקבלות בדיוק כמו לתלמיד.',
  kindFilterLabel: 'תלמידים או ארגונים',
  kindFilterTaught: 'תלמידים',
  kindFilterOrganizations: 'ארגונים',
  organizationBadge: 'ארגון',
  noOrganizationsYet: 'אין עדיין ארגונים',
  noOrganizationsHint: 'ארגון הוא מוסד שמשלם על הרכב: קונסרבטוריון, מגמת מוזיקה או מתנ״ס.'
} as const;

export type TranslationKey = keyof typeof he;
export type Dictionary = Record<TranslationKey, string>;
